import { calcIRR } from './calcIRR';
import { planMonthLearningMultiplier } from './hillPlanMonthLearning';
import {
  resolveActualFundingPct,
  resolveEffectiveRevSharePctFromParts,
} from './resolveProRataRevShare';

const MAX_CALENDAR_MONTHS = 72;

function capOrUnbounded(value) {
  if (value == null || value === '') {
    return 1e12;
  }
  return Number(value);
}

function padArray(values, length, fill = 0) {
  const out = [...(values || [])];
  while (out.length < length) {
    out.push(fill);
  }
  return out.slice(0, length);
}

export function resolveDealIrrMonthCount(inputs) {
  const curveLen = Math.max(
    inputs.cumulativeRoas.length,
    inputs.maxRoasByMob.length,
    12
  );
  return Math.min(
    MAX_CALENDAR_MONTHS,
    Math.max(1, inputs.marketing.length + curveLen)
  );
}

function incrementalFromCurve(curve, monthIndex) {
  const current = Number(curve[monthIndex]) || 0;
  if (!current) {
    return 0;
  }
  if (monthIndex === 0) {
    return current;
  }
  const previous = Number(curve[monthIndex - 1]) || 0;
  return current - previous;
}

function buildRoasCurve(inputs, monthCount) {
  const curve = padArray(inputs.cumulativeRoas, monthCount, 0);
  const incremental = Array.from({ length: monthCount }, (_, m) =>
    incrementalFromCurve(curve, m)
  );
  return { curve, incremental, saturation: null };
}

function buildLtvCurve(inputs, monthCount) {
  const curve = Array.from({ length: monthCount }, (_, m) =>
    inputs.hillMatched && m < inputs.maxRoasByMob.length
      ? Number(inputs.maxRoasByMob[m]) || 0
      : 0
  );
  const incremental = [...curve];
  const k = Number(inputs.k) || 0;
  const marketing = padArray(inputs.marketing, monthCount, 0);
  const originCount = Math.max(1, marketing.length);
  const saturation = Array.from({ length: originCount }, (_, origin) => {
    const spend = Number(marketing[origin]) || 0;
    return k && spend > 0 ? k / (k + spend) : 0;
  });
  const learningByOrigin = Array.from({ length: originCount }, (_, origin) =>
    planMonthLearningMultiplier(inputs.learning, origin)
  );
  return { curve, incremental, saturation, learningByOrigin };
}

function laggedRevenueShare(
  revenueShare,
  origin,
  monthIndex,
  marketingGrace,
  repaymentGrace
) {
  const startMonth = resolveCohortRepaymentStartMonth(
    origin,
    marketingGrace,
    repaymentGrace
  );
  if (monthIndex < startMonth) {
    return 0;
  }
  const repaymentPeriodIndex = monthIndex - startMonth;
  const revenueMonth = origin + repaymentPeriodIndex;
  return Number(revenueShare[origin]?.[revenueMonth]) || 0;
}

export function resolveCohortFundingMonth(origin, marketingGrace = 0) {
  const grace = Math.max(0, Math.floor(Number(marketingGrace) || 0));
  return origin + grace;
}

export function resolveCohortRepaymentStartMonth(
  origin,
  marketingGrace = 0,
  repaymentGrace = 0
) {
  return (
    resolveCohortFundingMonth(origin, marketingGrace) +
    Math.max(0, Math.floor(Number(repaymentGrace) || 0))
  );
}

/** Actual Twelve funding as a share of marketing spend paid in the same month. */
export function computeTwelveFundingPctRow(customerFunding, customerMarketing) {
  const funding = customerFunding || [];
  const marketing = customerMarketing || [];
  const monthCount = Math.max(funding.length, marketing.length);
  return Array.from({ length: monthCount }, (_, index) => {
    const spend = Math.abs(Number(marketing[index]) || 0);
    const draw = Number(funding[index]) || 0;
    if (spend <= 0 || draw <= 0) {
      return 0;
    }
    return draw / spend;
  });
}

function marketingSpendDueInMonth(inputs, monthIndex) {
  const grace = Math.max(0, Math.floor(Number(inputs.marketingGrace) || 0));
  const originCount = Math.max(1, inputs.marketing.length);
  let sum = 0;
  for (let origin = 0; origin < originCount; origin += 1) {
    if (origin >= inputs.commitmentMonths) {
      continue;
    }
    if (origin + grace === monthIndex) {
      sum += Number(inputs.marketing[origin]) || 0;
    }
  }
  return sum;
}

function sameMonthRepaymentBeforeDraw({
  monthIndex,
  originCount,
  revenueShare,
  exposure,
  cohortFee,
  repaymentGrace,
  marketingGrace,
}) {
  let total = 0;
  for (let origin = 0; origin < originCount; origin += 1) {
    const fundingMonth = resolveCohortFundingMonth(origin, marketingGrace);
    if (monthIndex <= fundingMonth) {
      continue;
    }
    const prevExposure = Number(exposure[origin]?.[monthIndex - 1]) || 0;
    const fee = Number(cohortFee[origin]?.[monthIndex]) || 0;
    const lagged = laggedRevenueShare(
      revenueShare,
      origin,
      monthIndex,
      marketingGrace,
      repaymentGrace
    );
    total += Math.min(lagged, Math.max(0, prevExposure + fee));
  }
  return total;
}

function computeDraw({
  monthIndex,
  inputs,
  originCount,
  monthCount,
  revenueShare,
  exposure,
  cohortFee,
  customerFunding,
  totalExposure,
  interest,
}) {
  const marketing = marketingSpendDueInMonth(inputs, monthIndex);
  if (marketing <= 0) {
    return 0;
  }
  const prevDraw =
    monthIndex === 0 ? 0 : Number(customerFunding[monthIndex - 1]) || 0;
  const momCap =
    monthIndex === 0 || !inputs.maxDrawIncrease || prevDraw === 0
      ? 1e12
      : prevDraw * (1 + inputs.maxDrawIncrease);
  const prevExposure =
    monthIndex === 0 ? 0 : Number(totalExposure[monthIndex - 1]) || 0;
  const headroom = Math.max(
    0,
    capOrUnbounded(inputs.maxExposure) -
      prevExposure -
      interest +
      sameMonthRepaymentBeforeDraw({
        monthIndex,
        originCount,
        revenueShare,
        exposure,
        cohortFee,
        repaymentGrace: inputs.grace,
        marketingGrace: inputs.marketingGrace,
      })
  );
  return Math.max(
    0,
    Math.min(
      marketing * inputs.fundingCap,
      capOrUnbounded(inputs.maxMonthlyDraw),
      headroom,
      momCap
    )
  );
}

function facilityFeeAtMonth(monthIndex, inputs) {
  const start = Math.round(Number(inputs.facilityFeeStart) || 0);
  const installments = Math.max(0, Number(inputs.facilityFeeInstallments) || 0);
  if (installments <= 0 || !inputs.facilitySize) {
    return 0;
  }
  if (monthIndex >= start && monthIndex < start + installments) {
    return (inputs.facilitySize * inputs.facilityFeePct) / installments;
  }
  return 0;
}

function monthlyIrrAt(inputs, monthIndex) {
  const perMonth = inputs.monthlyIrrByMonth?.[monthIndex];
  if (perMonth != null && Number.isFinite(Number(perMonth))) {
    return Number(perMonth);
  }
  return Number(inputs.monthlyIrr) || 0;
}

function averageFundedCohortMonthlyIrr({
  monthlyIrrRow,
  customerFunding,
  marketing,
  inputs,
  originCount,
}) {
  const grace = Math.max(0, Math.floor(Number(inputs.marketingGrace) || 0));
  const commitmentMonths =
    inputs.commitmentMonths == null ? originCount : Number(inputs.commitmentMonths);
  let sum = 0;
  let count = 0;
  for (let origin = 0; origin < originCount; origin += 1) {
    if (origin >= commitmentMonths) {
      continue;
    }
    if (!(Number(marketing[origin]) || 0)) {
      continue;
    }
    const fundingMonth = resolveCohortFundingMonth(origin, grace);
    if (!(Number(customerFunding[fundingMonth]) || 0)) {
      continue;
    }
    sum += Number(monthlyIrrRow[origin]) || 0;
    count += 1;
  }
  if (!count) {
    return 0;
  }
  return sum / count / 100;
}

export { averageFundedCohortMonthlyIrr };

function grossRevenue({
  monthIndex,
  originCount,
  marketing,
  incremental,
  saturation,
  learningByOrigin,
  mode,
}) {
  let total = 0;
  for (let origin = 0; origin <= monthIndex && origin < originCount; origin += 1) {
    const mob = monthIndex - origin;
    const incr = Number(incremental[mob]) || 0;
    const spend = Number(marketing[origin]) || 0;
    if (mode === 'ltv') {
      const sat = Number(saturation?.[origin]) || 0;
      const learn = Number(learningByOrigin?.[origin]) || 1;
      total += spend * incr * sat * learn;
    } else {
      total += spend * incr;
    }
  }
  return total;
}

function resolveDealIrrCohortRevShareDecimal(inputs, origin, effectiveRevShareByOrigin) {
  if (effectiveRevShareByOrigin[origin] != null) {
    return effectiveRevShareByOrigin[origin];
  }
  const configured =
    inputs.revShareByMonth?.[origin] != null
      ? inputs.revShareByMonth[origin] * 100
      : inputs.revShare != null
        ? inputs.revShare * 100
        : null;
  return (
    resolveEffectiveRevSharePctFromParts({
      configuredRevSharePct: configured,
      actualFundingPct: null,
      fallbackFundingPct: inputs.fundingCap * 100,
    }) / 100
  );
}

function recomputeCohortRevenueShareRow({
  origin,
  monthCount,
  mode,
  inputs,
  incremental,
  saturation,
  learningByOrigin,
  revenueShare,
  revShareDecimal,
}) {
  for (let monthIndex = origin; monthIndex < monthCount; monthIndex += 1) {
    revenueShare[origin][monthIndex] = computeCohortRevenueShare({
      mode,
      origin,
      monthIndex,
      inputs,
      incremental,
      saturation,
      learningByOrigin,
      revShareDecimal,
    });
  }
}

function computeCohortRevenueShare({
  mode,
  origin,
  monthIndex,
  inputs,
  incremental,
  saturation,
  learningByOrigin,
  revShareDecimal,
}) {
  if (monthIndex < origin) {
    return 0;
  }
  const spend = Number(inputs.marketing[origin]) || 0;
  const mob = monthIndex - origin;
  const incr = Number(incremental[mob]) || 0;
  const netShare = (1 - inputs.cogs) * revShareDecimal;
  if (mode === 'ltv') {
    const sat = Number(saturation?.[origin]) || 0;
    const learn = Number(learningByOrigin?.[origin]) || 1;
    return spend * incr * sat * learn * netShare;
  }
  return spend * incr * netShare;
}

function computeCohortGrossRevenue({
  mode,
  origin,
  monthIndex,
  inputs,
  incremental,
  saturation,
  learningByOrigin,
}) {
  if (monthIndex < origin) {
    return 0;
  }
  const spend = Number(inputs.marketing[origin]) || 0;
  const mob = monthIndex - origin;
  const incr = Number(incremental[mob]) || 0;
  if (mode === 'ltv') {
    const sat = Number(saturation?.[origin]) || 0;
    const learn = Number(learningByOrigin?.[origin]) || 1;
    return spend * incr * sat * learn;
  }
  return spend * incr;
}

function calendarMonthDate(firstMarketingMonth, monthIndex) {
  const base =
    firstMarketingMonth instanceof Date &&
    !Number.isNaN(firstMarketingMonth.getTime())
      ? firstMarketingMonth
      : new Date();
  return new Date(base.getFullYear(), base.getMonth() + monthIndex, 1);
}

/** Books cohort marketing spend in the customer cash row, shifted by marketing grace. */
export function buildCustomerMarketingOutflow(
  marketing,
  originCount,
  monthCount,
  marketingGrace = 0
) {
  const customerMarketing = Array(monthCount).fill(0);
  const grace = Math.max(0, Math.floor(Number(marketingGrace) || 0));
  for (let origin = 0; origin < originCount; origin += 1) {
    const spend = Number(marketing[origin]) || 0;
    if (!spend) {
      continue;
    }
    const paymentMonth = origin + grace;
    if (paymentMonth >= 0 && paymentMonth < monthCount) {
      customerMarketing[paymentMonth] -= spend;
    }
  }
  return customerMarketing;
}

function calendarYearSpan(firstMarketingMonth, monthCount) {
  const startMonth0 =
    firstMarketingMonth instanceof Date &&
    !Number.isNaN(firstMarketingMonth.getTime())
      ? firstMarketingMonth.getMonth()
      : 0;
  const endMonth0 = startMonth0 + Math.max(1, monthCount) - 1;
  return Math.floor(endMonth0 / 12) - Math.floor(startMonth0 / 12) + 1;
}

function yearAgg(values, calendarMonths, yearIndex, agg) {
  const year = calendarMonths[0].getFullYear() + yearIndex;
  const from = new Date(year, 0, 1);
  const to = new Date(year + 1, 0, 1);
  const picked = values.filter(
    (_, index) =>
      calendarMonths[index] >= from && calendarMonths[index] < to
  );
  if (!picked.length) {
    return 0;
  }
  if (agg === 'max') {
    return Math.max(...picked);
  }
  if (agg === 'avg') {
    const positive = picked.filter((value) => value > 0);
    if (!positive.length) {
      return 0;
    }
    return positive.reduce((sum, value) => sum + value, 0) / positive.length;
  }
  return picked.reduce((sum, value) => sum + value, 0);
}

export function computeDealIrrModel(inputs, { mode = 'roas' } = {}) {
  const monthCount = resolveDealIrrMonthCount(inputs);
  const originCount = Math.max(1, inputs.marketing.length);
  const marketing = padArray(inputs.marketing, monthCount, 0);

  const { curve, incremental, saturation, learningByOrigin } =
    mode === 'ltv'
      ? buildLtvCurve(inputs, monthCount)
      : { ...buildRoasCurve(inputs, monthCount), learningByOrigin: null };

  const revenueShare = Array.from({ length: originCount }, () =>
    Array(monthCount).fill(0)
  );
  const exposure = Array.from({ length: originCount }, () =>
    Array(monthCount).fill(0)
  );
  const cohortFee = Array.from({ length: originCount }, () =>
    Array(monthCount).fill(0)
  );
  const cohortRepayment = Array.from({ length: originCount }, () =>
    Array(monthCount).fill(0)
  );
  const cohortGrossRevenue = Array.from({ length: originCount }, () =>
    Array(monthCount).fill(0)
  );

  const customerFunding = Array(monthCount).fill(0);
  const customerMarketing = buildCustomerMarketingOutflow(
    marketing,
    originCount,
    monthCount,
    inputs.marketingGrace
  );
  const customerGross = Array(monthCount).fill(0);
  const customerNet = Array(monthCount).fill(0);
  const twelveFundingPct = Array(monthCount).fill(0);
  const customerRepayment = Array(monthCount).fill(0);
  const customerFees = Array(monthCount).fill(0);
  const customerNetCf = Array(monthCount).fill(0);
  const twelveOrigination = Array(monthCount).fill(0);
  const twelveRepayment = Array(monthCount).fill(0);
  const twelveFee = Array(monthCount).fill(0);
  const twelveNet = Array(monthCount).fill(0);
  const interest = Array(monthCount).fill(0);
  const totalExposure = Array(monthCount).fill(0);
  const monthlyIrrRow = Array(monthCount).fill(0);
  const calendarMonths = Array.from({ length: monthCount }, (_, m) =>
    calendarMonthDate(inputs.firstMarketingMonth, m)
  );
  const cohortFeeTotal = Array(monthCount).fill(0);
  const effectiveRevShareByOrigin = Array(originCount).fill(null);
  const effectiveRevSharePctByOrigin = Array(originCount).fill(0);

  for (let m = 0; m < monthCount; m += 1) {
    monthlyIrrRow[m] = monthlyIrrAt(inputs, m) * 100;

    for (let origin = 0; origin < originCount; origin += 1) {
      cohortGrossRevenue[origin][m] = computeCohortGrossRevenue({
        mode,
        origin,
        monthIndex: m,
        inputs,
        incremental,
        saturation,
        learningByOrigin,
      });
      revenueShare[origin][m] = computeCohortRevenueShare({
        mode,
        origin,
        monthIndex: m,
        inputs,
        incremental,
        saturation,
        learningByOrigin,
        revShareDecimal: resolveDealIrrCohortRevShareDecimal(
          inputs,
          origin,
          effectiveRevShareByOrigin
        ),
      });
    }

    for (let origin = 0; origin < originCount; origin += 1) {
      const fundingMonth = resolveCohortFundingMonth(origin, inputs.marketingGrace);
      if (m > fundingMonth) {
        cohortFee[origin][m] =
          (Number(exposure[origin][m - 1]) || 0) * monthlyIrrAt(inputs, origin);
      }
    }

    interest[m] = cohortFee.reduce(
      (sum, row) => sum + (Number(row[m]) || 0),
      0
    );

    customerFunding[m] = computeDraw({
      monthIndex: m,
      inputs,
      originCount,
      monthCount,
      revenueShare,
      exposure,
      cohortFee,
      customerFunding,
      totalExposure,
      interest: interest[m],
    });

    for (let origin = 0; origin < originCount; origin += 1) {
      const fundingMonth = resolveCohortFundingMonth(origin, inputs.marketingGrace);
      if (m !== fundingMonth) {
        continue;
      }
      const actualFundingPct = resolveActualFundingPct(
        marketing[origin],
        customerFunding[m]
      );
      const configured =
        inputs.revShareByMonth?.[origin] != null
          ? inputs.revShareByMonth[origin] * 100
          : inputs.revShare != null
            ? inputs.revShare * 100
            : null;
      const resolvedPct = resolveEffectiveRevSharePctFromParts({
        configuredRevSharePct: configured,
        actualFundingPct,
        fallbackFundingPct: inputs.fundingCap * 100,
      });
      effectiveRevShareByOrigin[origin] = resolvedPct / 100;
      effectiveRevSharePctByOrigin[origin] = resolvedPct;
      recomputeCohortRevenueShareRow({
        origin,
        monthCount,
        mode,
        inputs,
        incremental,
        saturation,
        learningByOrigin,
        revenueShare,
        revShareDecimal: effectiveRevShareByOrigin[origin],
      });
    }

    for (let origin = 0; origin < originCount; origin += 1) {
      if (m < origin) {
        continue;
      }
      const fundingMonth = resolveCohortFundingMonth(origin, inputs.marketingGrace);
      if (m < fundingMonth) {
        continue;
      }
      const lastExposure =
        m === fundingMonth ? 0 : Number(exposure[origin][m - 1]) || 0;
      const draw = m === fundingMonth ? customerFunding[m] : 0;
      const fee = cohortFee[origin][m];
      const lagged = laggedRevenueShare(
        revenueShare,
        origin,
        m,
        inputs.marketingGrace,
        inputs.grace
      );
      cohortRepayment[origin][m] = Math.min(
        lagged,
        Math.max(0, lastExposure + draw + fee)
      );
      exposure[origin][m] = Math.max(
        0,
        lastExposure + draw + fee - cohortRepayment[origin][m]
      );
    }

    cohortFeeTotal[m] = cohortFee.reduce(
      (sum, row) => sum + (Number(row[m]) || 0),
      0
    );

    customerGross[m] = grossRevenue({
      monthIndex: m,
      originCount,
      marketing,
      incremental,
      saturation,
      learningByOrigin,
      mode,
    });
    customerNet[m] = customerGross[m] * (1 - inputs.cogs);
    const marketingSpend = Math.abs(Number(customerMarketing[m]) || 0);
    const drawAmount = Number(customerFunding[m]) || 0;
    twelveFundingPct[m] =
      marketingSpend > 0 && drawAmount > 0 ? drawAmount / marketingSpend : 0;
    twelveRepayment[m] = cohortRepayment.reduce(
      (sum, row) => sum + (Number(row[m]) || 0),
      0
    );
    customerRepayment[m] = -twelveRepayment[m];
    customerFees[m] = -facilityFeeAtMonth(m, inputs);
    customerNetCf[m] =
      customerMarketing[m] +
      customerNet[m] +
      customerFunding[m] +
      customerRepayment[m] +
      customerFees[m];

    twelveOrigination[m] = -customerFunding[m];
    twelveFee[m] = -customerFees[m];
    twelveNet[m] = twelveOrigination[m] + twelveRepayment[m] + twelveFee[m];
    totalExposure[m] = exposure.reduce(
      (sum, row) => sum + (Number(row[m]) || 0),
      0
    );
  }

  const positiveExposure = totalExposure.filter((value) => value > 0);
  const maxExposureCalc =
    totalExposure.length > 0 ? Math.max(...totalExposure, 0) : 0;
  const avgExposure =
    positiveExposure.length > 0
      ? positiveExposure.reduce((sum, value) => sum + value, 0) /
        positiveExposure.length
      : 0;
  const outflow = twelveOrigination.reduce((sum, value) => sum + value, 0);
  const inflow =
    twelveRepayment.reduce((sum, value) => sum + value, 0) +
    twelveFee.reduce((sum, value) => sum + value, 0);
  const totalFacilityFee = inputs.facilitySize * inputs.facilityFeePct;
  const netCash = twelveNet.reduce((sum, value) => sum + value, 0);
  const irr = calcIRR(twelveNet);
  const yearCount = Math.min(
    8,
    Math.max(1, calendarYearSpan(inputs.firstMarketingMonth, monthCount))
  );

  const averageFinancingFeeRate = averageFundedCohortMonthlyIrr({
    monthlyIrrRow,
    customerFunding,
    marketing,
    inputs,
    originCount,
  });
  const financingIrrAnnual = (1 + averageFinancingFeeRate) ** 12 - 1;
  const facilityIrrAnnual = Number.isFinite(irr)
    ? irr - financingIrrAnnual
    : NaN;
  const creditTotalIrr = Number.isFinite(irr) ? irr : NaN;

  const summaryYears = Array.from({ length: yearCount }, (_, yearIndex) => ({
    year: calendarMonths[0].getFullYear() + yearIndex,
    cashAtRiskMax: yearAgg(totalExposure, calendarMonths, yearIndex, 'max'),
    cashAtRiskAvg: yearAgg(totalExposure, calendarMonths, yearIndex, 'avg'),
    outflow: yearAgg(twelveOrigination, calendarMonths, yearIndex, 'sum'),
    inflow: yearAgg(
      twelveRepayment.map((value, index) => value + twelveFee[index]),
      calendarMonths,
      yearIndex,
      'sum'
    ),
    netCash: yearAgg(twelveNet, calendarMonths, yearIndex, 'sum'),
    financingFeeCash: yearAgg(cohortFeeTotal, calendarMonths, yearIndex, 'sum'),
    facilityFeeCash: yearAgg(twelveFee, calendarMonths, yearIndex, 'sum'),
  }));

  const financingFeeCash = cohortFeeTotal.reduce((sum, value) => sum + value, 0);
  const facilityFeeCash = twelveFee.reduce((sum, value) => sum + value, 0);
  const totalFeeCash = financingFeeCash + facilityFeeCash;

  return {
    mode,
    monthCount,
    originCount,
    calendarMonths,
    monthLabels: Array.from({ length: monthCount }, (_, m) => `Month ${m}`),
    monthlyIrrRow,
    marketing,
    curve,
    incremental,
    saturation,
    learningByOrigin,
    customerMarketing,
    customerGross,
    customerNet,
    twelveFundingPct,
    customerFunding,
    customerRepayment,
    customerFees,
    customerNetCf,
    twelveOrigination,
    twelveRepayment,
    twelveFee,
    twelveNet,
    interest,
    exposure: totalExposure,
    cohortGrossRevenue,
    cohortRepaymentByOrigin: cohortRepayment,
    marketingGrace: inputs.marketingGrace,
    outputs: {
      maxExposureCalc,
      avgExposure,
      outflow,
      inflow,
      totalFacilityFee,
      irr,
      netCash,
    },
    summary: {
      maxExposure: maxExposureCalc,
      commitmentMonths: inputs.commitmentMonths,
      headlineOrigination:
        inputs.maxMonthlyDraw != null && inputs.commitmentMonths != null
          ? inputs.maxMonthlyDraw * inputs.commitmentMonths
          : inputs.facilitySize,
      financingFeeRate: averageFinancingFeeRate,
      financingIrrAnnual,
      facilityFeeRate: inputs.facilityFeePct,
      facilityIrrAnnual,
      creditTotalIrr,
      cashAtRiskMax: maxExposureCalc,
      cashAtRiskAvg: avgExposure,
      netCashReturn: netCash,
      cashReturnOnMax:
        maxExposureCalc > 0 ? 1 + netCash / maxExposureCalc : null,
      cashReturnOnAvg: avgExposure > 0 ? 1 + netCash / avgExposure : null,
      outflow,
      inflow,
      netCash,
      cashOnCash: outflow !== 0 ? inflow / Math.abs(outflow) : null,
      financingFeeCash,
      facilityFeeCash,
      totalFeeCash,
      feeOnAvg: avgExposure > 0 ? 1 + totalFeeCash / avgExposure : null,
      feeOnMax: maxExposureCalc > 0 ? 1 + totalFeeCash / maxExposureCalc : null,
      years: summaryYears,
    },
    cohortGrids: {
      revenueShare,
      exposure,
      cohortFee,
      cohortRepayment,
      cohortFeeTotal,
    },
  };
}
